import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown, Input } from 'antd'
import { CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { MAX_APP_TAGS, PRESET_APP_TAGS } from '../languages'

export default function AppTagsSelect({
  value,
  disabled,
  onChange,
}: {
  value: string[]
  disabled?: boolean
  onChange: (tags: string[]) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const full = value.length >= MAX_APP_TAGS

  const toggle = (tag: string) => {
    if (value.includes(tag)) {
      onChange(value.filter((x) => x !== tag))
      return
    }
    if (full) return
    onChange([...value, tag])
  }

  const remove = (tag: string) => onChange(value.filter((x) => x !== tag))

  const addCustom = () => {
    const tag = draft.trim()
    if (!tag || full || value.includes(tag)) {
      setDraft('')
      return
    }
    onChange([...value, tag])
    setDraft('')
  }

  const panel = (
    <div
      style={{
        width: 560,
        maxWidth: '90vw',
        background: '#fff',
        border: '1px solid var(--color-border-line)',
        borderRadius: 14,
        boxShadow: '0px 16px 22px -6px rgba(28, 28, 28, 0.16)',
        overflow: 'hidden',
        paddingBottom: 20,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 20px',
        }}
      >
        <span style={{ flex: 1, fontWeight: 500, fontSize: 14, lineHeight: '22px' }}>
          {t('versionForm.selectAppTags')}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            width: 16,
            height: 16,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloseOutlined style={{ fontSize: 12 }} />
        </button>
      </div>
      <div style={{ height: 1, background: 'rgba(0,0,0,0.04)' }} />
      <div
        style={{
          padding: '20px 20px 0',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        {PRESET_APP_TAGS.map((tag) => {
          const checked = value.includes(tag)
          const blocked = full && !checked
          return (
            <button
              key={tag}
              type="button"
              disabled={disabled || blocked}
              onClick={() => toggle(tag)}
              style={{
                width: '100%',
                height: 38,
                padding: '0 12px',
                borderRadius: 14,
                border: 'none',
                background: 'rgba(0,0,0,0.02)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: blocked || disabled ? 'not-allowed' : 'pointer',
                opacity: blocked ? 0.45 : 1,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  border: checked ? 'none' : '1.5px solid var(--color-border-line)',
                  background: checked
                    ? 'var(--color-brand-indigo)'
                    : '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {checked && <CheckOutlined style={{ fontSize: 10, color: '#fff' }} />}
              </span>
              <span style={{ fontSize: 13, lineHeight: '20px' }}>{tag}</span>
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        {!disabled && (
          <Dropdown
            open={open}
            onOpenChange={setOpen}
            popupRender={() => panel}
            trigger={['click']}
            placement="bottomLeft"
          >
            <button
              type="button"
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                border: '1px solid var(--color-border-line)',
                background: 'var(--color-background-module-container)',
                backdropFilter: 'blur(15px)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
              aria-label="add tag"
            >
              <PlusOutlined style={{ fontSize: 14 }} />
            </button>
          </Dropdown>
        )}
        {value.map((tag) => (
          <span
            key={tag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 28,
              padding: '0 12px',
              borderRadius: 8,
              background: '#EFEFEF',
              fontSize: 13,
              lineHeight: '20px',
            }}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(tag)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                  width: 14,
                  height: 14,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloseOutlined style={{ fontSize: 10 }} />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <Input
          placeholder={t('versionForm.tagsPlaceholder')}
          value={draft}
          disabled={full}
          maxLength={20}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={addCustom}
          style={{
            height: 50,
            borderRadius: 10,
            borderColor: 'var(--color-border-line)',
            background: 'var(--color-background-module-container)',
            opacity: full ? 0.55 : 0.9,
          }}
        />
      )}
    </div>
  )
}
